import { saveAs } from "file-saver";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { ToastService } from "../../AppToast";
import { localized, useTranslation } from "../../shared/language";
import {
  ERROR,
  getDetailJobById,
  IN_PROGRESS,
  resumeJob,
  SUCCESS,
} from "../../service/ExportService";
import { useDialogExportContext } from "../context/DialogExportContext";
import { CopyToClipboard } from "react-copy-to-clipboard";

interface Props {
  visible: boolean;
  setVisible: (value: boolean) => void;
  status: "success" | "error" | "doing";
  numberRecord: number;
  onSubmit: () => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const downloadExcelFromBase64 = (
  base64Data: string,
  fileName: string
) => {
  // 1. Xử lý chuỗi Base64: Loại bỏ phần header "data:application/..." nếu có
  const cleanBase64 = base64Data.includes(",")
    ? base64Data.split(",")[1]
    : base64Data;

  // 2. Định nghĩa MIME type cho Excel
  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

  // 3. Chuyển đổi Base64 sang Blob
  // Hàm atob() giải mã chuỗi base64 thành chuỗi nhị phân
  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: EXCEL_TYPE });

  // 4. Dùng FileSaver để lưu file
  saveAs(blob, fileName);
};

export const downloadZipFromBase64 = (base64Data: string, fileName: string) => {
  // 1. Làm sạch chuỗi Base64 (bỏ header data:application/zip;base64,... nếu có)
  const cleanBase64 = base64Data.includes(",")
    ? base64Data.split(",")[1]
    : base64Data;

  // 2. Định nghĩa MIME type cho ZIP
  // Đây là điểm khác biệt chính so với Excel
  const ZIP_TYPE = "application/zip";

  // 3. Chuyển đổi Base64 sang Blob
  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  // Tạo Blob với type là ZIP
  const blob = new Blob([byteArray], { type: ZIP_TYPE });

  const fileSizeInBytes = blob.size; // Kích thước tính bằng Bytes
  const readableSize = formatFileSize(fileSizeInBytes);

  // 4. Kiểm tra xem tên file có đuôi .zip chưa, nếu chưa thì thêm vào
  const finalFileName = fileName.endsWith(".zip")
    ? fileName
    : `${fileName}.zip`;

  // 5. Lưu file
  saveAs(blob, finalFileName);
};

export const DialogExport = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { visible, setVisible, status } = props;

  const { jobDetail, setStatus, setJobDetail, setMinimize, minimize } = useDialogExportContext();

  const [openErrorDialog, setopenErrorDialog] = useState<boolean>(false);

  const hidden = minimize ? "hidden" : "";
  const minimizeDoing = minimize;

  const formatter = new Intl.NumberFormat(i18n.language === "en" ? "en-US" : "vi-VN");

  const TENNANT_ID = localStorage.getItem("iam-m-invoice-react.v1.0.0_tenant");

  const getDetailJobRunning = async (id: string) => {
    if (!id || !TENNANT_ID) return;
    setStatus("doing");
    const res = await getDetailJobById(id);
    setJobDetail(res);
    const isExpired = res.expiredTime && new Date(res.expiredTime) < new Date();

    if (res.status === IN_PROGRESS && isExpired) {
      setStatus("error");
      setVisible(true);
      return;
    }

    if (res.status === IN_PROGRESS) {
      setStatus("doing");
      setVisible(true);
      setTimeout(() => {
        getDetailJobRunning(id);
      }, 3000);
    }

    if (res.status === SUCCESS) {
      setStatus("success");
      setVisible(true);
    }
    if (res.status === ERROR) {
      setStatus("error");
      setVisible(true);
    }
  };

  // useEffect(() => {
  //   if (jobDetail?.jobId) {
  //     getDetailJobRunning(jobDetail?.jobId!);
  //   }
  // }, [jobDetail?.jobId]);

  const resumeJobFail = async () => {
    try {
      if (!jobDetail) {
        ToastService.error(localized("common.jobNotFound"));
        return;
      }
      await resumeJob(jobDetail.jobId);
      ToastService.success(localized("common.resumeSuccess"));
      setStatus("doing");
      setVisible(true);
      getDetailJobRunning(jobDetail.jobId);
    } catch (error) {
      ToastService.error(localized("common.retryError"));
    }
  };

  const onCloseDialog = () => {
    setVisible(false);
    localStorage.removeItem("jobIdRunning");
    setStatus("success");
    setJobDetail(null);
  };

  const ErrorDialog = () => {
    return (
      <Dialog
        visible={openErrorDialog}
        header={false}
        onHide={() => setopenErrorDialog(false)}
        closable={false}
        footer={null}
        style={{
          width: "800px",
          height: "500px",
        }}
      >
        <div className="flex flex-column h-full">
          <div className="flex">
            <div className="flex-1 flex items-center text-center justify-content-center text-red-500 font-bold text-xl">
              {t("common.exportErrorTitle")}
            </div>
            <i
              className="pi pi-times cursor-pointer"
              onClick={() => setopenErrorDialog(false)}
            />
          </div>
          <div className="flex items-center justify-content-center mt-2">
            {t("common.exportErrorMessage")}
          </div>
          <div className="border-1 w-full flex-grow-1 overflow-y-auto p-2 my-2 px-4">
            <div className="font-bold text-lg text-black">{t("common.errorDetails")}</div>
            <div className="text-black-alpha-90">{jobDetail?.errorMessage}</div>
          </div>

          <div className="flex items-center justify-content-end">
            <CopyToClipboard text={jobDetail?.errorMessage || ""}
              onCopy={() => ToastService.success(localized("common.copiedError"))}>
              <Button
                label={t("common.copy")}
                className=""
                icon="pi pi-clone"
              // onClick={() => {
              //   if (jobDetail?.errorMessage) {
              //     navigator.clipboard.writeText(jobDetail?.errorMessage);
              //     ToastService.success("Đã sao chép lỗi");
              //   }
              // }}
              />
            </CopyToClipboard>

          </div>
        </div>
      </Dialog>
    );
  };

  const getFileSize = () => {
    if (!jobDetail?.result?.fileContent) return "0 Bytes";
    const base64Data = jobDetail.result.fileContent;
    const cleanBase64 = base64Data.includes(",")
      ? base64Data.split(",")[1]
      : base64Data;
    const padding = (cleanBase64.match(/=/g) || []).length;
    const bytes = (cleanBase64.length * 3) / 4 - padding;
    return formatFileSize(bytes);
  };

  const getFileReportName = (exportType: number) => {
    switch (exportType) {
      case 1:
        return t("menu.pmktReport");
      case 2:
        return t("menu.invoiceDetailReport");
      case 4:
        return t("menu.invoiceSummaryReport");
      case 8:
        return t("menu.usageReport");
      default:
        return t("menu.reports");
    }
  };

  return (
    <Dialog
      showHeader={false}
      visible={visible}
      onHide={onCloseDialog}
      position="bottom-left"
      modal={false}
      draggable={false}
      resizable={false}
      style={{
        width: `${minimizeDoing ? "max-content" : "max-content"}`,
        height: `${minimizeDoing ? "50px" : "max-content"}`,
        marginBottom: "65px",
        overflow: "hidden",
        transition: "all 0.5s ease",
      }}
      closable={false}
      footer={null}
      contentStyle={{ padding: "1rem", overflow: "hidden" }}
    >
      <div className={`overflow-hidden`}>
        <div
          className={`flex flex-row align-items-center ${minimizeDoing ? "justify-content-center" : "justify-content-between"
            } mb-2`}
        >
          <div
            className={`text-center ${minimizeDoing ? "" : "flex-1"} row-gap-4`}
          >
            {status === "doing" && (
              <div className="text-primary flex flex-row justify-content-center row-gap-4 align-items-center">
                <img
                  src={"/assets/layout/images/hourglass.svg"}
                  alt="hourglass"
                  className={`${!minimize ? "hidden" : ""}`}
                />
                <div className="text-lg font-bold text-black-alpha-90">
                  {minimize ? (
                    <div className="flex align-items-center">
                      {t("common.exportSuccess")} &nbsp;
                      <div className="text-primary mr-10">
                        {formatter.format(jobDetail?.totalExported)}
                      </div>
                      &nbsp;{t("common.records")}
                    </div>
                  ) : (
                    <div className="text-lg">{t("common.exportProgress")}</div>
                  )}
                </div>
              </div>
            )}
            {status === "success" && (
              <div className="text-primary flex flex-row justify-content-center row-gap-4 align-items-center">
                <i
                  className={`pi pi-check-circle mr-4 ${!minimize ? "hidden" : ""
                    }`}
                  style={{ fontSize: "2rem", color: "green" }}
                />
                <div className="text-lg font-bold text-black-alpha-90">
                  {minimize ? (
                    <p>
                      {t("common.exportSuccess")} &nbsp;
                      <span className="text-primary mr-10">
                        {formatter.format(jobDetail?.totalExported)}
                      </span>
                      &nbsp;{t("common.records")}
                    </p>
                  ) : (
                    <div className="text-lg">{t("common.exportSuccess")}</div>
                  )}
                </div>
              </div>
            )}
            {status === "error" && (
              <div className="text-primary flex flex-row justify-content-center row-gap-4 align-items-center">
                <i
                  className={`pi pi-times-circle mr-4 ${!minimize ? "hidden" : ""
                    }`}
                  style={{ fontSize: "2rem", color: "red" }}
                />
                <div className="text-lg font-bold text-black-alpha-90">
                  {minimize ? (
                    <div className="flex align-items-center">
                      {t("common.exportFailed")} &nbsp;
                    </div>
                  ) : (
                    <div className="text-lg">{t("common.exportErrorTitle")}!</div>
                  )}
                </div>
              </div>
            )}
            {/* {status === "success" && (
              <span className={`text-xl font-bold ml-8 ${hidden}`}>
                Dữ liệu đã sẵn sàng!
              </span>
            )} */}
            {/* {status === "error" && (
              <span className="text-xl font-bold ml-8">
                Lỗi kết xuất dữ liệu!
              </span>
            )} */}
          </div>
          <div className={`${minimize ? "flex" : "flex absolute right-0"}`}>
            <div
              className="cursor-pointer"
              onClick={() => {
                setMinimize(!minimize);
              }}
            >
              {!minimize ? (
                <img
                  src={"/assets/layout/images/minimize.svg"}
                  alt="maximize"
                  className="h-6 w-6 ml-4"
                />
              ) : (
                <img
                  src={"/assets/layout/images/maximize.svg"}
                  alt="minimize"
                  className="h-6 w-6 ml-4"
                />
              )}
            </div>
            {status !== "doing" && (
              <i
                className="pi pi-times cursor-pointer h-6 w-6 ml-4"
                onClick={() => {
                  setVisible(false);
                  localStorage.removeItem("jobIdRunning");
                  setStatus("success");
                  setJobDetail(null);
                }}
              />
            )}
          </div>
        </div>
        <div
          className={`flex-grow-1 ${minimize ? "hidden" : "flex"} align-items-center justify-content-center row-gap-2 ${minimize ? "flex-row" : "flex-column"
            }`}
        >
          <div>
            {status === "doing" && (
              <div
                className={`flex flex-column align-items-center justify-content-center row-gap-4 w-max`}
              >
                <img
                  src={"/assets/layout/images/hourglass.svg"}
                  alt="hourglass"
                  className={`${hidden} h-2 w-2`}
                />
                <div
                  className={`${minimize ? "hidden" : ""
                    } border-1 border-round-xs p-2 font-bold w-max`}
                >
                  {getFileReportName(jobDetail?.exportOption?.exportType)}
                </div>
                <div className={`${hidden}`}>
                  {t("common.exportRunningMessage")}
                </div>
                <div
                  className={`${hidden} w-max text-black-alpha-90 font-semibold`}
                >
                  {t("common.currentlyExported")}&nbsp;
                  <span className="text-primary font-bold">
                    {formatter.format(jobDetail?.totalExported)}
                  </span>
                  &nbsp;{t("common.records")}
                </div>
              </div>
            )}
            {status === "success" && (
              <div>
                <div className="flex flex-column align-items-center justify-content-center row-gap-4 pt-2">
                  <i
                    className="pi pi-check-circle"
                    style={{ fontSize: "3rem", color: "green" }}
                  />
                  <div className="text-xl">
                      {t("common.exportSuccessMessage")} {" "}
                    <span className="text-primary font-semibold">
                      {formatter.format(jobDetail?.totalExported)}
                    </span>{" "}
                    {t("common.records")}
                  </div>
                  <div className="flex flex-row align-items-center justify-content-center gap-3 w-full">
                    <div className="font-bold text-lg overflow-hidden text-overflow-ellipsis white-space-nowrap">
                      {jobDetail?.result.fileName}
                    </div>
                    <div className="h-2 w-2 border-round bg-gray-400 p-1 w-max "></div>
                    <div className="font-medium">{getFileSize()}</div>
                  </div>
                  {jobDetail?.exportFilePaths?.length > 1 && (
                    <div className="text-base">
                      ({t("common.filesInZip", { count: jobDetail?.exportFilePaths?.length })})
                    </div>
                  )}
                  <div>
                    <Button
                      label={t("common.download")}
                      className=""
                      onClick={() => {
                        try {
                          if (jobDetail?.exportFilePaths?.length > 1) {
                            downloadZipFromBase64(
                              jobDetail.result.fileContent,
                              jobDetail.result.fileName
                            );
                          } else {
                            downloadExcelFromBase64(
                              jobDetail.result.fileContent,
                              jobDetail.result.fileName
                            );
                          }
                        } catch (error) {
                          ToastService.error(localized("common.retryError"));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {status === "error" && (
              <div>
                <div className="flex flex-column align-items-center justify-content-center row-gap-4 pt-2 w-25rem">
                  <i
                    className="pi pi-times-circle"
                    style={{ fontSize: "3rem", color: "red" }}
                  />
                  <div className="text-xl">{t("common.exportErrorTitle")}</div>
                  <div>{t("common.exportErrorMessage")}</div>
                  <div className="flex flex-column align-items-center justify-content-center gap-2">
                    <Button
                      icon="pi pi-info-circle"
                      label={t("common.viewDetails")}
                      className="p-button-link text-underline"
                      onClick={() => setopenErrorDialog(true)}
                    />
                    <Button
                      label={t("common.continue")}
                      className=""
                      onClick={resumeJobFail}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ErrorDialog />
    </Dialog>
  );
};
